import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {
  MetaDataField,
  NativeRedactionSettings,
  RedactionData,
  TagItem,
} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {
  convertPTagsToLineBreaks,
  mapIdsToTags,
  wrapTextWithTag,
} from '../../CreateNewProductionHelpers';
import {ComponentType} from '../../CreateNewProductionUtils';

export const DEFAULT_NATIVE_REDACTION_TEXT = '[REDACTED]';
export const NATIVE_REDACTION_STYLE_TEXT = 'Text';
export const DEFAULT_NATIVE_REDACTION_STYLE = NATIVE_REDACTION_STYLE_TEXT;

export const NATIVE_REDACTION_STYLE_OPTIONS: ISelectItem[] = [
  {value: 'Text', label: 'Text'},
  {value: 'Black', label: 'Black'},
  {value: 'White', label: 'White'},
];

const NATIVE_REDACTION_STYLE_VALUES = new Set(
  NATIVE_REDACTION_STYLE_OPTIONS.map((option) => String(option.value))
);

const NATIVE_REDACTION_API_CODES = new Map<string, string>([
  ['TXT', 'Text'],
  ['BLK', 'Black'],
  ['WHT', 'White'],
]);

const getNativeValueFromApiItem = (item: {
  value: string;
  text: string;
}): string | null => {
  const nativeValue = String(item.value ?? '').trim();
  if (NATIVE_REDACTION_STYLE_VALUES.has(nativeValue)) {
    return nativeValue;
  }

  const apiCode = String(item.text ?? '')
    .trim()
    .toUpperCase();
  const mappedValue = NATIVE_REDACTION_API_CODES.get(apiCode);
  return mappedValue && NATIVE_REDACTION_STYLE_VALUES.has(mappedValue)
    ? mappedValue
    : null;
};

export const normalizeNativeRedactionStyleValue = (style?: string): string => {
  if (!style) {
    return DEFAULT_NATIVE_REDACTION_STYLE;
  }

  const trimmedStyle = style.trim();
  const apiCode = trimmedStyle.toUpperCase();
  const mappedValue = NATIVE_REDACTION_API_CODES.get(apiCode);
  if (mappedValue) {
    return mappedValue;
  }

  if (NATIVE_REDACTION_STYLE_VALUES.has(trimmedStyle)) {
    return trimmedStyle;
  }

  return DEFAULT_NATIVE_REDACTION_STYLE;
};

export const mapNativeRedactionStyleOptions = (
  nativeRedactionStyleList?: {value: string; text: string}[]
): ISelectItem[] => {
  if (!nativeRedactionStyleList?.length) {
    return NATIVE_REDACTION_STYLE_OPTIONS;
  }

  const mappedOptions = nativeRedactionStyleList
    .map((item) => {
      const nativeValue = getNativeValueFromApiItem(item);
      if (!nativeValue) {
        return null;
      }

      return {
        value: nativeValue,
        label: nativeValue,
      } satisfies ISelectItem;
    })
    .filter(
      (option): option is {value: string; label: string} => option !== null
    );

  return mappedOptions.length > 0
    ? mappedOptions
    : NATIVE_REDACTION_STYLE_OPTIONS;
};

export const mapNativeRedactionStyleToApiValue = (
  nativeStyle: string | undefined,
  nativeRedactionStyleList?: {value: string; text: string}[]
): string => {
  const normalizedNativeValue = normalizeNativeRedactionStyleValue(nativeStyle);

  if (!nativeRedactionStyleList?.length) {
    return normalizedNativeValue;
  }

  const matchingItem = nativeRedactionStyleList.find(
    (item) => getNativeValueFromApiItem(item) === normalizedNativeValue
  );

  return matchingItem?.text?.trim() || normalizedNativeValue;
};

export const isNativeTextRedactionStyle = (style?: string): boolean =>
  normalizeNativeRedactionStyleValue(style) === NATIVE_REDACTION_STYLE_TEXT;
export const REDACTION_WITHOUT_TAGS_ID = '-2';

export interface NativeRedactionItem {
  id: string;
  redactionText: string;
  redactionLogicId: number;
  selectedRedactionList: string;
  selectedTags: TagItem[];
}

export interface NativeRedactionSettingsState {
  isBurnRedactionSelected: boolean;
  isRedactionByAnnotation: boolean;
  redactionType: number;
  redactionStyle: string;
  redactionDataList: NativeRedactionItem[];
  specifiedRedactionList: string;
}

export const createDefaultNativeRedactionSettings =
  (): NativeRedactionSettingsState => ({
    isBurnRedactionSelected: false,
    isRedactionByAnnotation: true,
    redactionType: 1,
    redactionStyle: DEFAULT_NATIVE_REDACTION_STYLE,
    redactionDataList: [],
    specifiedRedactionList: '',
  });

export const buildSpecifiedRedactionList = (
  selectedTags: TagItem[],
  isRedactionWithoutTags: boolean
): string => {
  const tagIds = selectedTags.map((tag) => tag.tagId).join(',');

  if (!isRedactionWithoutTags) {
    return tagIds;
  }

  return tagIds
    ? `${tagIds},${REDACTION_WITHOUT_TAGS_ID}`
    : REDACTION_WITHOUT_TAGS_ID;
};

export const hydrateNativeRedactionSettings = (
  settings: NativeRedactionSettings | undefined,
  redactionTagTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[]
): {
  redactionSettings: NativeRedactionSettingsState;
  redactionGlobalSelectedTags: TagItem[];
  isRedactionWithoutTags: boolean;
} => {
  if (!settings) {
    return {
      redactionSettings: createDefaultNativeRedactionSettings(),
      redactionGlobalSelectedTags: [],
      isRedactionWithoutTags: true,
    };
  }

  const specifiedRedactionList = settings.specifiedRedactionList || '';
  const hasRedactionWithoutTags = specifiedRedactionList
    .split(',')
    .map((value) => value.trim())
    .includes(REDACTION_WITHOUT_TAGS_ID);
  const isEmpty =
    !specifiedRedactionList || specifiedRedactionList.trim() === '';

  const filteredList = specifiedRedactionList
    .split(',')
    .map((value) => value.trim())
    .filter((id) => id !== REDACTION_WITHOUT_TAGS_ID && id !== '')
    .join(',');

  const redactionDataList =
    settings.redactionConfigurations?.redactionDataList?.map((item, index) => {
      const selectedTags =
        item.selectedTags && item.selectedTags.length > 0
          ? item.selectedTags
          : mapIdsToTags(
              item.selectedRedactionList || '',
              redactionTagTreeData
            );

      return {
        id: `redaction-${index}`,
        redactionText: convertPTagsToLineBreaks(item.redactionText || ''),
        redactionLogicId: item.redactionLogicId || 0,
        selectedRedactionList: item.selectedRedactionList || '',
        selectedTags,
      } satisfies NativeRedactionItem;
    }) || [];

  const isBurnRedactionSelected = settings.isBurnRedactionSelected ?? false;

  return {
    redactionSettings: {
      isBurnRedactionSelected,
      isRedactionByAnnotation: settings.isRedactionByAnnotation ?? true,
      redactionType: settings.redactionType ?? 1,
      redactionStyle: isBurnRedactionSelected
        ? normalizeNativeRedactionStyleValue(settings.redactionStyle)
        : DEFAULT_NATIVE_REDACTION_STYLE,
      redactionDataList,
      specifiedRedactionList,
    },
    redactionGlobalSelectedTags: mapIdsToTags(
      filteredList,
      redactionTagTreeData
    ),
    isRedactionWithoutTags: isEmpty ? true : hasRedactionWithoutTags,
  };
};

export const serializeNativeRedactionSettings = (
  redactionSettings: NativeRedactionSettingsState,
  redactionGlobalSelectedTags: TagItem[],
  isRedactionWithoutTags: boolean,
  nativeRedactionStyleList?: {value: string; text: string}[]
): NativeRedactionSettings => ({
  ctype: '',
  isBurnRedactionSelected: redactionSettings.isBurnRedactionSelected,
  isRedactionByAnnotation: redactionSettings.isRedactionByAnnotation,
  specifiedRedactionList: buildSpecifiedRedactionList(
    redactionGlobalSelectedTags,
    isRedactionWithoutTags
  ),
  redactionType: redactionSettings.redactionType,
  redactionStyle: mapNativeRedactionStyleToApiValue(
    redactionSettings.redactionStyle,
    nativeRedactionStyleList
  ),
  redactionConfigurations: {
    ctype: '',
    componentType: ComponentType.NATIVE,
    redactionDataList: redactionSettings.redactionDataList.map((item) => ({
      redactionText: wrapTextWithTag(item.redactionText, 'p'),
      redactionLogicId: item.redactionLogicId,
      selectedRedactionList: item.selectedTags
        .map((tag) => tag.tagId)
        .join(','),
      selectedTags: item.selectedTags,
    })) as RedactionData[],
  },
});

export const mapMetadataToSelectItems = (
  fields: MetaDataField[] | undefined
): ISelectItem[] =>
  fields?.map((field) => ({
    value: field.fieldLabel,
    label: field.fieldLabel,
  })) || [];
